/**
 * Atari 2600 ROMs that CANNOT be faithfully recorded as replays.
 *
 * Solo replays log input as a per-player uint16 RetroPad joypad bitmask (see
 * the lockstep input bypass in docker/memory-export.patch). Games that read a
 * NON-joypad controller — analog paddle/driving pots, the XG-1 light gun, or
 * the keypad / Sesame-Street "Kids Controller" — never touch that bitmask, so a
 * recorded log would replay with a frozen/centered controller and desync. This
 * is the same root limitation that excludes N64 (analog stick) and C64
 * (keyboard) entirely; for Atari it only affects a minority of titles.
 *
 * Recording is the only producer of replays, so blocking these ROMs in
 * App._shouldRecordSolo() guarantees no broken replay is ever created. Such
 * games fall back to the normal self-driving libretro path (no recording),
 * exactly like a non-allowlisted system.
 *
 * Keyed by the 8-char FNV-1a `romHash` (EmulatorCore.hashROM). Hashes are
 * sourced verbatim from rom-index/atari2600.json — the same value the client
 * computes at load time and stores in replays.rom_hash — so matches are exact.
 * To extend: add a game's `hash` from rom-index/atari2600.json. The list errs
 * toward confidently-non-joypad titles; joystick-primary hybrids (e.g. Star
 * Raiders) are intentionally treated as recordable.
 */
export const ATARI_NON_JOYPAD_ROM_HASHES = new Set([
  // ── Paddle (analog potentiometer) ──────────────────────────────
  '6fc34ac4', // Breakout (1978) (Atari)
  'e683510b', // Breakout - Breakaway IV (2)
  '08b37b2c', // Super Breakout (1982) (Atari)
  '0854d5b4', // Kaboom! (1)
  'e589cc6c', // Kaboom! (2)
  '05cebfc0', // Warlords (1981) (Atari)
  'ecd9fd14', // Night Driver (1980) (Atari)
  '334bc0b0', // Night Driver (4k Version)
  '1f1187c0', // Circus Atari (1980) (Atari)
  '06dd1630', // Demons to Diamonds (1982) (Atari)
  '7c87b35c', // Video Olympics / Pong (1977) (Atari)
  '04a10134', // Video Olympics - Pong Sports (2)
  '57249348', // Steeplechase (1)
  '42504cd0', // Steeplechase (1983) (Video Gems) (PAL)
  '50c5fc0c', // Steeplechase (2)
  '05d597cc', // Street Racer (1977) (Atari)
  'b44e9a48', // Street Racer - Speedway II (2)
  '8e5c4474', // Canyon Bomber (1979) (Atari)
  'ec63cb34', // Canyon Bomber (4k version)
  '53db024c', // Casino (1979) (Atari)
  '8a67b6d0', // Bumper Bash (1983) (Spectravideo)
  '30d76484', // Eggomania (1982) (U.S. Games)
  'b1ee4418', // Picnic (1982) (U.S. Games)
  '673cd7c0', // Piece o' Cake (1982) (U.S. Games)
  'fc4a9804', // PizzaChef (Piece o' Cake homebrew)
  'c262e598', // Backgammon (1979) (Atari)
  '334c5208', // Bachelor Party (1982) (Mystique)
  '5ac9be18', // Gigolo (1982) (PlayAround)
  'e11daa0c', // Beat 'Em & Eat 'Em (1982) (Mystique)
  'e91a02f4', // Cathouse Blues (1982) (PlayAround)
  '41238c1c', // Philly Flasher (1982) (PlayAround)
  // ── Driving controller (analog spinner) ────────────────────────
  'b7b12b1c', // Indy 500 (1977) (Atari)
  'b106e41c', // Indy 500 (4k Version)
  // ── Light gun (XG-1) ───────────────────────────────────────────
  '35599aa8', // Sentinel (1990) (Atari)
  '2d1260e4', // Shooting Arcade (1989) (Atari) (Prototype)
  // ── Keypad / Kids Controller ───────────────────────────────────
  '8c5cc294', // Basic Programming (1979) (Atari)
  'fd2c2d38', // Brain Games (1)
  '5d285608', // Brain Games (2)
  'b6497538', // Game of Concentration (1980) (Atari)
  'd540ab2c', // Alpha Beam with Ernie (1983) (Atari)
  'e04db7cd', // Big Bird's Egg Catch (1983) (Atari)
  '356408c4', // Cookie Monster Munch (1983) (Atari)
  '880fc260', // Oscar's Trash Race (1983) (Atari)
  '24974334', // Peek-A-Boo (1984) (Atari) (Prototype)
]);
